import * as Yup from 'yup';
import AllForm from '../../Stateless/Form';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from '@mui/material';
import { useAllMerchant } from '../../Query/MerchantQuery';
import { UseCreateChoice, useEditChoice } from '../../Query/ChoiceQuery';
import { useThemeColors } from '../../../hooks/useThemeColors';
import { useAtom } from 'jotai';
import { choiceAtom } from './ManageChoicesTable';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useAllStores } from '../../Query/ManageStoreQuery';
import { useStoreManagementParams } from '../StoreManagement/useStoreManagementParams';
import { FieldProps, FormikProps } from 'formik';
import { COLORS } from '../../../theme/themeConstants';

interface MerchantData {
  id: string;
  name: string;
}

const AddChoice = ({ onCancel }: { onCancel: () => void }) => {
  const [choiceDataId] = useAtom(choiceAtom);
  const { warning } = useThemeColors();
  const formikRef = useRef<FormikProps<any>>(null);

  // Track current store type for merchant fetching
  const [currentStoreType, setCurrentStoreType] = useState(choiceDataId ? choiceDataId.storeType : "");

  const { mutate: CreateChoice, isPending: CreateChoiceLoading } =
    UseCreateChoice();

  const { mutate: EditChoice, isPending: EditChoiceLoading } = useEditChoice();

  const { data: merchantData, refetch } = useAllMerchant({
    initialData: {
      storeType: currentStoreType || undefined,
    },
  });
  const [isMerchantLoading, setIsMerchantLoading] = useState(false);
  const [options, setMerchantOptions] = useState<
    { label: string; value: string }[]
  >([]);

  // Store options state
  const [storeOptions, setStoreOptions] = useState<{ label: string; value: string }[]>([]);

  const { params, dispatch } = useStoreManagementParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const {
    data: storesData, isLoading: isStoresLoading
  } = useAllStores(memoizedParams);


  useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      const options = storesData.data.map((store: any) => ({
        value: store.id,
        label: store.restaurantDetails.storeName,
      }));
      setStoreOptions(options);
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);

  // Update options when merchantData changes
  useEffect(() => {
    if (merchantData) {
      const merchantOptions = merchantData.map((item: MerchantData) => ({
        value: item.id,
        label: item.name,
      }));
      setMerchantOptions(merchantOptions);
    }
  }, [merchantData]);

  const refetchMerchants = async () => {
    setIsMerchantLoading(true);
    try {
      await refetch();
    } finally {
      setIsMerchantLoading(false);
    }
  };

  // Validation schema depends on add/edit mode
  const validationSchema = useMemo(() =>
    Yup.object().shape({
      choiceNameEnglish: Yup.string()
        .required('Please enter Choice Name')
        .matches(/^[A-Za-z][A-Za-z\s]*$/, 'Choice Name should only contain letters and spaces, and not start with a space')
        .max(100, 'Choice Name cannot exceed 100 characters') // corrected max from 50 to 100
        .test(
          'not-only-spaces',
          'Choice Name cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      choiceNameArabic: Yup.string()
        .required('Please enter Choice Name in Arabic')
        .matches(
          /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
          'Name should only contain Arabic letters and spaces, and not start with a space'
        )
        .max(50, 'Choice Name cannot exceed 50 characters')
        .test(
          'not-only-spaces',
          'Name cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      price: Yup.number()
        .typeError('Price must be a number')
        .required('Please enter the price')
        .positive('Price must be greater than 0'),
      merchantId: choiceDataId ? Yup.string() : Yup.string().required('Please select Merchant'),
      storeType: choiceDataId ? Yup.string() : Yup.string().required('Please select Store Type'),
      storeId: choiceDataId
        ? Yup.string()
        : Yup.string().when(['merchantId', 'storeType'], {
          is: (merchantId: string, storeType: string) => merchantId && merchantId.length > 0 && storeType && storeType.length > 0,
          then: (schema) => schema.required('Please select Store'),
          otherwise: (schema) => schema.optional(),
        }),
    }),
    [choiceDataId]);

  // Handle form reset
  const handleReset = () => {
    if (formikRef.current) {
      formikRef.current.resetForm();
      // Reset store type state
      setCurrentStoreType("");
      // Clear store management params
      dispatch({ type: 'RESET' });
      // Clear store options
      setStoreOptions([]);
      // Clear merchant options
      setMerchantOptions([]);
    }
  };

  return (
    <>
      {AllForm({
        initialValues: {
          choiceNameEnglish: choiceDataId
            ? choiceDataId?.choiceNameEnglish
            : '',
          choiceNameArabic: choiceDataId ? choiceDataId?.choiceNameArabic : '',
          merchantId: choiceDataId ? choiceDataId?.merchantId : '',
          price: choiceDataId ? choiceDataId?.price : null,
          storeType: choiceDataId ? choiceDataId?.storeType : '',
          storeId: choiceDataId ? choiceDataId?.storeId : '',
        },
        validationSchema,
        validateOnChange: false,
        validateOnBlur: true,
        enableReinitialize: false, // Changed to false to prevent unwanted resets
        onSubmit: async (values: any) => {
          // Check if stores are available for the selected merchant
          const noStoresAvailable = values.merchantId && storeOptions.length === 0 && !isStoresLoading;

          if (!choiceDataId && noStoresAvailable) {
            console.log('No stores available for the selected merchant');
            return;
          }

          // For edit, use values from choiceDataId; for add, use form values
          const modifiedValues = choiceDataId
            ? {
              storeType: choiceDataId.storeType,
              storeId: choiceDataId.storeId,
              merchantId: choiceDataId.merchantId,
              choiceNameEnglish: values.choiceNameEnglish,
              choiceNameArabic: values.choiceNameArabic,
              price: +values.price,
              addedBy: 'Admin',
            }
            : {
              storeType: values.storeType,
              storeId: values.storeId,
              merchantId: values.merchantId,
              choiceNameEnglish: values.choiceNameEnglish,
              choiceNameArabic: values.choiceNameArabic,
              price: +values.price,
              addedBy: 'Admin',
            };

          try {
            if (choiceDataId) {
              EditChoice(
                {
                  id: choiceDataId.id || '',
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.log(error);
                  },
                }
              );
            } else {
              CreateChoice(
                { modifiedValues, storeId: values.storeId },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.log(error);
                  },
                }
              );
            }
          } catch (error) {
            console.log(error);
          }
        },

        fields: [
          // Only show these fields if not in edit mode
          ...(!choiceDataId ? [
            {
              name: 'storeType',
              label: 'Select Store Type:',
              type: 'customWithError',
              isRequired: true,
              disabled: false,
              component: ({ field, form }: { field: FieldProps['field']; form: FormikProps<any> }) => (
                <FormControl fullWidth>
                  <Select
                    {...field}
                    onChange={async (e) => {
                      const value = e.target.value as string;
                      
                      // Update current store type state first
                      setCurrentStoreType(value);
                      
                      // Set the field value
                      form.setFieldValue('storeType', value);
                      
                      // Clear dependent fields when store type changes
                      form.setFieldValue('merchantId', '');
                      form.setFieldValue('storeId', '');
                      form.setFieldTouched('merchantId', false);
                      form.setFieldTouched('storeId', false);
                      form.setFieldError('merchantId', undefined);
                      form.setFieldError('storeId', undefined);
                      
                      // Clear store management params
                      dispatch({ type: 'RESET' });
                      // Clear store options
                      setStoreOptions([]);
                      // Clear merchant options
                      setMerchantOptions([]);
                      
                      // Mark field as touched and validate after a small delay
                      setTimeout(() => {
                        form.setFieldTouched('storeType', true, false);
                        if (value) {
                          form.validateField('storeType');
                        }
                      }, 100);
                      
                      await refetchMerchants();
                    }}
                    displayEmpty
                    sx={{ minWidth: 'unset', width: 400 }}
                  >
                    <MenuItem value="" disabled>
                      <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                        -- Select --
                      </span>
                    </MenuItem>
                    {[
                      { label: 'Restaurant', value: 'Restaurant' },
                      { label: 'Grocery Store', value: 'GroceryStore' },
                      { label: 'Pharmacy Store', value: 'PharmacyStore' },
                    ].map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ),

            },
            {
              name: 'merchantId',
              label: 'Select Merchant:',
              type: 'customWithError',
              isRequired: true,
              disabled: !currentStoreType || isMerchantLoading,
              component: ({ form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                const isDisabled = !currentStoreType || isMerchantLoading;
                return (
                  <Box sx={{ width: 400 }}>
                    <Autocomplete
                      options={options}
                      getOptionLabel={(option) => option.label}
                      value={options.find(opt => opt.value === form.values.merchantId) || null}
                      onChange={(_, newValue) => {
                        const merchantValue = newValue ? newValue.value : '';
                        form.setFieldValue('merchantId', merchantValue);
                        
                        // Clear store selection when merchant changes
                        form.setFieldValue('storeId', '');
                        form.setFieldTouched('storeId', false);
                        form.setFieldError('storeId', undefined);
                        
                        dispatch({ type: 'SET_MERCHANT_ID', payload: merchantValue });
                        
                        // Mark field as touched and validate after a small delay
                        setTimeout(() => {
                          form.setFieldTouched('merchantId', true, false);
                          if (merchantValue) {
                            form.validateField('merchantId');
                          }
                        }, 100);
                      }}
                      disabled={isDisabled}
                      fullWidth
                      sx={{
                        width: '100%', // Ensure Autocomplete itself expands
                        '& .MuiInputBase-root': {
                          width: '100%', // Force input root width
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            !currentStoreType
                              ? 'Select Store Type first'
                              : 'Select Merchant'
                          }
                          variant="outlined"
                          fullWidth
                          disabled={isDisabled}
                          error={false}
                          helperText=""
                          sx={{
                            filter: isDisabled ? 'blur(1px)' : 'none',
                            backgroundColor: isDisabled ? '#f5f5f5' : 'inherit',
                            minWidth: 'unset',
                            width: 400,
                          }}
                        />
                      )}
                    />
                  </Box>
                );
              },
            },
            {
              name: 'storeId',
              label: 'Select Store:',
              type: 'customWithError',
              isRequired: true,
              disabled: false, // Will be handled inside component
              component: ({ form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                const isDisabled = !form.values.merchantId || isStoresLoading;
                const noStoresAvailable = !isStoresLoading && form.values.merchantId && storeOptions.length === 0;
                const shouldDisable = isDisabled || noStoresAvailable;
                return (
                  <Box sx={{ width: 400 }}>
                    <Autocomplete
                      options={storeOptions}
                      getOptionLabel={(option) => option.label}
                      value={storeOptions.find(opt => opt.value === form.values.storeId) || null}
                      onChange={(_, newValue) => {
                        form.setFieldValue('storeId', newValue ? newValue.value : '');
                        
                        // Mark field as touched and validate after a small delay
                        setTimeout(() => {
                          form.setFieldTouched('storeId', true, false);
                          if (newValue) {
                            form.validateField('storeId');
                          }
                        }, 100);
                      }}
                      disabled={shouldDisable}
                      fullWidth
                      sx={{
                        width: '100%', // Ensure Autocomplete itself expands
                        '& .MuiInputBase-root': {
                          width: '100%', // Force input root width
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={
                            !form.values.merchantId
                              ? 'Select Merchant first'
                              : isStoresLoading
                                ? 'Loading Stores...'
                                : noStoresAvailable
                                  ? 'No store available'
                                  : 'Select Store'
                          }
                          error={false}
                          helperText=""
                          sx={{
                            filter: shouldDisable ? 'blur(1px)' : 'none',
                            backgroundColor: shouldDisable ? '#f5f5f5' : 'inherit',
                            minWidth: 'unset',
                            width: 400,
                          }}
                          variant="outlined"
                          fullWidth
                          disabled={shouldDisable}
                        />
                      )}
                    />
                  </Box>
                );
              },
            },
          ] : []),
          // Always show these fields
          {
            name: 'choiceNameEnglish',
            label: 'Choice Name  (In English):',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'choiceNameArabic',
            label: 'Choice Name  (In Arabic):',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'price',
            label: 'Price  (In JOD):',
            type: 'number',
            isRequired: true,
          },
        ],

        customButton: (formikProps: any) => {
          // Store formik reference for reset functionality
          formikRef.current = formikProps;

          return (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  backgroundColor: COLORS.primary.main,
                  '&:hover': {
                    backgroundColor: COLORS.primary.dark,
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  // Touch all fields to show validation errors
                  const fieldNames = ['choiceNameEnglish', 'choiceNameArabic', 'price'];
                  if (!choiceDataId) {
                    fieldNames.push('storeType', 'merchantId', 'storeId');
                  }
                  fieldNames.forEach(fieldName => {
                    if (typeof formikProps.setFieldTouched === 'function') {
                      formikProps.setFieldTouched(fieldName, true, false);
                    }
                  });
                  if (typeof formikProps.handleSubmit === 'function') {
                    formikProps.handleSubmit();
                  }
                }}
                disabled={CreateChoiceLoading || EditChoiceLoading}
              >
                {CreateChoiceLoading || EditChoiceLoading ? (
                  <>
                    <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                    {choiceDataId ? 'Updating...' : 'Creating...'}
                  </>
                ) : choiceDataId ? (
                  'Update'
                ) : (
                  'Create'
                )}
              </Button>

              {!choiceDataId && (
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleReset}
                  sx={{
                    textTransform: 'none',
                    fontSize: 14,
                    px: 3,
                    py: 1,
                    borderColor: COLORS.secondary.main,
                    color: COLORS.secondary.main,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&:disabled': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    },
                  }}
                >
                  Reset
                </Button>
              )}
              <Button
                type="button"
                variant="outlined"
                color="error"
                onClick={onCancel}
                sx={{
                  bgcolor: warning.main,
                  color: '#fff',
                  '&:hover': {
                    bgcolor: warning.dark,
                    color: '#fff',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          );
        },
      })()}
    </>
  );
};

export default AddChoice;
